// Anticipos de sueldo (día ~21): depósitos a trabajadores ↔ columna Anticipos del libro ↔ egreso Alegra (Iter 354)
import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtFecha } from '../../lib/utils';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Loader2, Link2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const CLP = (v) => (v || 0).toLocaleString('es-CL');
const ddmm = (f) => fmtFecha(f);
const ESTADO = {
  ok: { cls: 'bg-emerald-100 text-emerald-700', label: 'Descontado OK' },
  diferencia: { cls: 'bg-orange-100 text-orange-700', label: 'Monto distinto' },
  sin_deposito: { cls: 'bg-slate-200 text-slate-600', label: 'Descontado, sin depósito en cartola' },
  no_descontado: { cls: 'bg-red-100 text-red-700', label: 'Pagado y NO descontado' },
};
const dif = (v) => (v === 0 ? 'text-emerald-600' : 'text-red-600');

export function AnticiposSection({ nominaId, periodo, onChanged }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [data, setData] = useState(null);
  const [alegraIds, setAlegraIds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aplicando, setAplicando] = useState(false);

  useEffect(() => {
    if (!periodo) return;
    const [y, m] = periodo.split('-');
    setDesde(`${y}-${m}-15`); setHasta(`${y}-${m}-28`); setAlegraIds(null);
  }, [periodo, nominaId]);

  const load = useCallback(async () => {
    if (!nominaId || !desde || !hasta) return;
    setLoading(true);
    try {
      const params = { desde, hasta };
      if (alegraIds !== null) params.alegra_ids = alegraIds.join(',');
      const r = await api.get(`/conciliacion-bancaria/remuneraciones/${nominaId}/anticipos`, { params });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error cruzando anticipos'); }
    finally { setLoading(false); }
  }, [nominaId, desde, hasta, alegraIds]);
  useEffect(() => { load(); }, [load]);

  const toggleAlegra = (e) => {
    const actuales = data.egresos_alegra.filter((x) => x.incluido).map((x) => x.alegra_id);
    setAlegraIds(e.incluido ? actuales.filter((id) => id !== e.alegra_id) : [...actuales, e.alegra_id]);
  };

  const aplicar = async () => {
    const n = data.trabajadores.filter((f) => f.estado === 'ok').reduce((s, f) => s + f.depositos.filter((d) => d.estado_mov === 'pendiente').length, 0);
    if (!window.confirm(`Se conciliarán ${n} depósitos pendientes de anticipo que calzan exacto con el libro. ¿Continuar?`)) return;
    setAplicando(true);
    try {
      const r = await api.post(`/conciliacion-bancaria/remuneraciones/${nominaId}/anticipos/aplicar`, { desde, hasta });
      toast.success(`${r.data.conciliados} anticipos conciliados, ${r.data.marcados} marcados`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error aplicando'); }
    finally { setAplicando(false); }
  };

  if (!nominaId) return null;
  return (
    <Card data-testid="anticipos-section"><CardContent className="space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <p className="text-sm font-semibold">Anticipos de sueldo {data?.periodo_label || ''}</p>
          <p className="text-[11px] text-muted-foreground">Depósitos a trabajadores en la 2ª quincena ↔ columna "Anticipos" del libro ↔ egreso de anticipos en Alegra. El informe de anticipos se podrá subir aquí cuando lo tengas.</p>
        </div>
        <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="input-ant-desde" />
        <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="input-ant-hasta" />
        <Button variant="outline" size="sm" onClick={aplicar} disabled={aplicando || !data} data-testid="btn-aplicar-anticipos">
          {aplicando ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Link2 className="mr-1 h-4 w-4" />} Aplicar anticipos OK
        </Button>
      </div>
      {loading && <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="ant-loading"><Loader2 className="h-3 w-3 animate-spin" /> Cruzando anticipos...</p>}
      {data && !loading && (
        <>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-5" data-testid="ant-totales">
            {[
              ['Anticipos según libro', `$${CLP(data.totales.libro)}`, '', 'libro'],
              ['Depósitos banco', `$${CLP(data.totales.banco)}`, dif(data.totales.dif_banco_libro), 'banco'],
              ['Egreso anticipos Alegra', `$${CLP(data.totales.alegra)}`, dif(data.totales.dif_alegra_banco), 'alegra'],
              ['Banco − Libro', `$${CLP(data.totales.dif_banco_libro)}`, dif(data.totales.dif_banco_libro), 'dif-banco-libro'],
              ['Alegra − Banco', `$${CLP(data.totales.dif_alegra_banco)}`, dif(data.totales.dif_alegra_banco), 'dif-alegra-banco'],
            ].map(([l, v, cls, tid]) => (
              <div key={l} className="rounded-md border p-2" data-testid={`ant-card-${tid}`}>
                <p className="text-[10px] uppercase text-muted-foreground">{l}</p>
                <p className={`text-sm font-bold ${cls}`}>{v}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground" data-testid="ant-resumen">
            {data.resumen.ok} descontados OK · <span className="text-red-700">{data.resumen.no_descontado} pagados y NO descontados</span> · {data.resumen.diferencia} con monto distinto · {data.resumen.sin_deposito} descontados sin depósito visible en la cartola · {data.resumen.sin_libro} depósitos a personas fuera del libro
          </p>
          {data.trabajadores.length === 0 ? (
            <p className="text-xs text-muted-foreground" data-testid="ant-empty">Sin anticipos en el libro ni depósitos a trabajadores en esta ventana (sube la cartola de ese mes para cruzar).</p>
          ) : (
            <table className="w-full text-xs" data-testid="ant-tabla">
              <thead><tr className="border-b text-left text-muted-foreground">
                <th className="py-1 pr-2">Trabajador</th><th className="py-1 pr-2 text-right">Libro</th><th className="py-1 pr-2 text-right">Banco</th><th className="py-1 pr-2">Estado</th><th className="py-1">Depósitos</th>
              </tr></thead>
              <tbody>
                {data.trabajadores.map((f) => (
                  <tr key={f.rut} className="border-b last:border-0" data-testid={`ant-row-${f.rut}`}>
                    <td className="py-1 pr-2">{f.nombre} <span className="text-muted-foreground">· {f.rut}</span></td>
                    <td className="py-1 pr-2 text-right">${CLP(f.anticipos)}</td>
                    <td className="py-1 pr-2 text-right font-medium">${CLP(f.banco)}</td>
                    <td className="py-1 pr-2"><Badge className={`${ESTADO[f.estado].cls} text-[10px]`} data-testid={`ant-estado-${f.rut}`}>{ESTADO[f.estado].label}</Badge></td>
                    <td className="py-1">{f.depositos.map((d) => <span key={d.mov_id} className="mr-2 whitespace-nowrap">{ddmm(d.fecha)} ${CLP(d.monto)}{d.estado_mov !== 'pendiente' && <CheckCircle2 className="ml-0.5 inline h-3 w-3 text-emerald-600" />}</span>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="grid gap-3 lg:grid-cols-2">
            <div data-testid="ant-sin-libro">
              <p className="text-xs font-semibold">Depósitos a personas que no están en el libro ({data.depositos_sin_libro.length})</p>
              {data.depositos_sin_libro.map((m) => <p key={m.mov_id} className="text-xs"><span className="text-muted-foreground">{ddmm(m.fecha)}</span> {m.detalle.replace(/^TRASPASO A:/i, '')} · <b>${CLP(m.monto)}</b></p>)}
            </div>
            <div data-testid="ant-egresos-alegra">
              <p className="text-xs font-semibold">Egresos de anticipos en Alegra (marca los que respaldan estos depósitos)</p>
              {data.egresos_alegra.length === 0 && <p className="text-xs text-muted-foreground">Ninguno detectado en la ventana.</p>}
              {data.egresos_alegra.map((e) => (
                <label key={e.alegra_id} className={`flex cursor-pointer items-start justify-between gap-2 text-xs ${e.incluido ? '' : 'text-muted-foreground'}`} data-testid={`ant-egreso-${e.alegra_id}`}>
                  <span className="flex gap-1.5"><input type="checkbox" checked={e.incluido} onChange={() => toggleAlegra(e)} className="mt-0.5" data-testid={`ant-egreso-check-${e.alegra_id}`} /><span><span className="text-muted-foreground">{ddmm(e.fecha)}</span> #{e.alegra_id} {e.descripcion || e.contacto} <span className="text-muted-foreground">({e.cuentas.join(', ')})</span></span></span>
                  <b className="whitespace-nowrap">${CLP(e.monto)}</b>
                </label>
              ))}
            </div>
          </div>
        </>
      )}
    </CardContent></Card>
  );
}
